import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import { Star, Check, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function StarRating({ value, onChange, label }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="mb-4">
      <p className="text-sm font-medium mb-2" style={{ color: 'var(--ink)' }}>{label}</p>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map(n => (
          <button key={n}
            onClick={() => onChange(n)}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            className="transition-transform hover:scale-110"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}>
            <Star size={28} style={{
              fill:   (hover || value) >= n ? '#D4AF37' : 'none',
              color:  (hover || value) >= n ? '#D4AF37' : '#D1D5DB',
              strokeWidth: 1.5,
            }} />
          </button>
        ))}
      </div>
    </div>
  );
}

export default function AvisPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    note: 5, note_livraison: 5, note_proprete: 5,
    note_facilite: 5, note_experience: 5, commentaire: '',
  });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND}/api/avis/invite/${token}`)
      .then(r => r.json())
      .then(d => { setInfo(d); if (d.already_submitted) setSubmitted(true); })
      .catch(() => setInfo({ error: true }))
      .finally(() => setLoading(false));
  }, [token]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (form.note === 0) { toast.error('Veuillez attribuer une note'); return; }
    setSending(true);
    try {
      const res = await fetch(`${BACKEND}/api/avis/public`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, ...form }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      setSubmitted(true);
      toast.success('Merci pour votre avis ! 🎉');
    } catch (err) {
      toast.error(err.message || 'Erreur');
    }
    setSending(false);
  }

  if (loading) return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <Header />
      <div className="text-center py-20">
        <div className="animate-spin w-8 h-8 rounded-full mx-auto"
          style={{ border: '3px solid var(--border)', borderTopColor: 'var(--teal)' }} />
      </div>
    </div>
  );

  if (info?.error || !info) return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <Header />
      <div className="text-center py-20 px-4">
        <AlertCircle size={48} className="mx-auto mb-4" style={{ color: '#EF4444' }} />
        <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--ink)' }}>Lien invalide</h2>
        <p style={{ color: 'var(--text-muted)' }}>Ce lien d'invitation est invalide ou expiré.</p>
      </div>
      <Footer />
    </div>
  );

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <Header />
      <div className="container mx-auto px-4 py-10 max-w-lg">
        {submitted ? (
          /* ── Confirmation ── */
          <div className="text-center py-16">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
              style={{ background: 'rgba(31,157,122,0.1)', border: '2px solid #1F9D7A' }}>
              <Check size={36} style={{ color: '#1F9D7A' }} />
            </div>
            <h2 className="text-2xl font-bold mb-3" style={{ color: 'var(--ink)' }}>
              Merci pour votre avis ! 🎉
            </h2>
            <p className="mb-6" style={{ color: 'var(--text-muted)' }}>
              Votre avis sera publié après modération. Il aide les autres clients à choisir.
            </p>
            <a href="https://g.page/r/show-evenement/review" target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm"
              style={{ background: '#D4AF37', color: '#0B1E20', textDecoration: 'none' }}>
              <Star size={15} /> Laisser aussi un avis Google
            </a>
          </div>
        ) : (
          /* ── Formulaire ── */
          <div>
            <div className="text-center mb-8">
              {info.product_image && (
                <img src={info.product_image} alt={info.product_nom}
                  className="w-24 h-24 rounded-2xl object-cover mx-auto mb-4" loading="lazy" />
              )}
              <h1 className="text-2xl font-bold mb-2" style={{ color: 'var(--ink)' }}>
                Votre avis sur {info.product_nom}
              </h1>
              <p style={{ color: 'var(--text-muted)' }}>
                Bonjour {info.client_nom}, comment s'est passée votre location ?
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="rounded-2xl p-5"
                style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
                <StarRating label="Note globale ⭐" value={form.note}
                  onChange={v => setForm(f => ({ ...f, note: v }))} />
                <StarRating label="Qualité de la livraison 🚚" value={form.note_livraison}
                  onChange={v => setForm(f => ({ ...f, note_livraison: v }))} />
                <StarRating label="Propreté du matériel ✨" value={form.note_proprete}
                  onChange={v => setForm(f => ({ ...f, note_proprete: v }))} />
                <StarRating label="Facilité d'utilisation 🎮" value={form.note_facilite}
                  onChange={v => setForm(f => ({ ...f, note_facilite: v }))} />
                <StarRating label="Expérience globale 🌟" value={form.note_experience}
                  onChange={v => setForm(f => ({ ...f, note_experience: v }))} />
              </div>

              <div className="rounded-2xl p-5"
                style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--ink)' }}>
                  Votre commentaire
                </label>
                <textarea
                  value={form.commentaire}
                  onChange={e => setForm(f => ({ ...f, commentaire: e.target.value }))}
                  placeholder="Partagez votre expérience... (optionnel)"
                  rows={4}
                  className="w-full px-3 py-2.5 rounded-xl text-sm resize-none"
                  style={{ border: '1px solid var(--border)', color: 'var(--ink)', outline: 'none', background: 'var(--bg)' }}
                />
              </div>

              <button type="submit" disabled={sending}
                className="w-full py-4 rounded-2xl font-bold text-base"
                style={{ background: '#1F9D7A', color: '#fff', border: 'none', cursor: sending ? 'wait' : 'pointer',
                         boxShadow: '0 8px 24px rgba(31,157,122,0.3)', opacity: sending ? 0.7 : 1 }}>
                {sending ? 'Envoi en cours...' : '⭐ Publier mon avis'}
              </button>
            </form>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
